
import React, { useState, useEffect } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Book, Eye, EyeOff, LoaderCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';

const signupSchema = z.object({
  fullName: z.string().min(2, { message: 'Full name must be at least 2 characters' }),
  email: z.string().email({ message: 'Please enter a valid email address' }),
  organization: z.string().optional(),
  password: z.string().min(6, { message: 'Password must be at least 6 characters' }),
  confirmPassword: z.string().min(6, { message: 'Password must be at least 6 characters' }),
  invitationKey: z.string().optional()
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type SignupFormValues = z.infer<typeof signupSchema>;

const Signup: React.FC = () => {
  const { isAuthenticated, isAdmin, signup } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessingInvitation, setIsProcessingInvitation] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [invitationKey, setInvitationKey] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  // Get query parameters
  const queryParams = new URLSearchParams(location.search);
  const email = queryParams.get('email') || '';
  const name = queryParams.get('name') || '';
  const organization = queryParams.get('organization') || '';
  const key = queryParams.get('key') || '';
  
  // Initialize the form
  const form = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      fullName: name,
      email: email,
      organization: organization,
      password: '',
      confirmPassword: '',
      invitationKey: key
    },
  });

  // Process invitation from Zendesk authentication
  useEffect(() => {
    const processZendeskInvitation = async () => {
      if (email && !key) {
        setIsProcessingInvitation(true);
        try {
          // Try to get invitation key from Zendesk auth
          const response = await supabase.functions.invoke('zendesk-auth', {
            body: JSON.stringify({
              email,
              password: 'dummy-password', // Not actually used for this flow
              getInvitation: true
            }),
          });
  
          if (response.error) {
            throw new Error(response.error);
          }
  
          if (response.data?.invitationKey) {
            setInvitationKey(response.data.invitationKey);
            form.setValue('invitationKey', response.data.invitationKey);
            navigate(`/invitation?key=${response.data.invitationKey}`);
          }
        } catch (error) {
          console.error('Error processing Zendesk invitation:', error);
        } finally {
          setIsProcessingInvitation(false);
        }
      }
    };
    
    processZendeskInvitation();
  }, [email, key, form, navigate]);

  // Redirect if already logged in
  if (isAuthenticated) {
    return <Navigate to={isAdmin ? "/admin/dashboard" : "/dashboard"} />;
  }

  // Redirect to invitation page if we have a key
  useEffect(() => {
    if (key) {
      navigate(`/invitation?key=${key}`);
    }
  }, [key, navigate]);

  const onSubmit = async (values: SignupFormValues) => {
    // We should never get here since we redirect to invitation page
    // But just in case, let's handle it
    if (values.invitationKey) {
      navigate(`/invitation?key=${values.invitationKey}`);
      return;
    }
    
    toast.info('An invitation is required to create an account.');
  };

  if (isProcessingInvitation) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <LoaderCircle className="h-8 w-8 animate-spin text-primary mr-2" />
        <span>Processing invitation...</span>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-12">
      <div className="absolute inset-0 z-0 bg-[radial-gradient(circle_at_bottom_left,theme(colors.primary.DEFAULT/10%),transparent_40%)]"></div>
      
      {/* Back to Home button */}
      <div className="absolute top-4 left-4 z-10">
        <Button variant="ghost" size="sm" asChild className="gap-1">
          <Link to="/">
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
        </Button>
      </div>
      
      <Card className="mx-auto w-full max-w-md animate-scale-in border-gray-8">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center">
            <Link to="/" className="mb-4 inline-flex items-center space-x-2">
              <Book className="h-6 w-6 text-yellow" />
              <span className="text-xl font-semibold text-yellow">C TWO</span>
            </Link>
          </div>
          <CardTitle className="text-2xl">Invitation Required</CardTitle>
          <CardDescription>
            An invitation is required to create an account
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <Alert className="mb-6">
            <AlertDescription>
              To create an account, you need an invitation from an administrator. 
              If you have an invitation link, please use it to sign up.
            </AlertDescription>
          </Alert>
          
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="invitationKey"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Invitation Key</FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        placeholder="Enter your invitation key" 
                        className="bg-gray-9 border-gray-7"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <Button 
                type="submit" 
                className="w-full bg-yellow text-black hover:bg-yellow-light"
              >
                Continue with Invitation
              </Button>
              
              <div className="mt-6 text-center text-sm">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="font-medium text-yellow hover:underline"
                >
                  Log in
                </Link>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Signup;
